import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnAccent, btnPrimary, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { useAuth } from "@/context/AuthContext";
import { TableCard, StatusBadge, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { Plus, Search, Users, Edit2, Trash2, HandCoins, Wallet } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", cnic: "", address: "", area: "", notes: "", credit_limit: 0, status: "active" };
const PAGE = 20;

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

export default function Clients() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "manager";
  const { data: outstanding } = useQuery({ queryKey: ["outstanding"], queryFn: () => api.get("/reports/outstanding").then((r) => r.data), enabled: canManage });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [q]);
  const fe = useFormErrors();

  const { data } = useQuery({
    queryKey: ["clients", q, page],
    queryFn: () => api.get("/clients", { params: { q: q || undefined, skip: page * PAGE, limit: PAGE } })
      .then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] ?? r.data.length) })),
    placeholderData: keepPreviousData,
  });
  const clients = data?.items ?? [];
  const total = data?.total ?? 0;

  const openNew = () => { setEditing(null); setForm(empty); fe.clear(); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); fe.clear(); setOpen(true); };

  const save = useMutation({
    mutationFn: () => editing ? api.put(`/clients/${editing.id}`, form) : api.post("/clients", form),
    onSuccess: () => { toast.success(editing ? "Client updated" : "Client added"); qc.invalidateQueries({ queryKey: ["clients"] }); setOpen(false); setForm(empty); setEditing(null); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/clients/${id}`),
    onSuccess: () => { toast.success("Client deleted"); qc.invalidateQueries({ queryKey: ["clients"] }); },
    onError: (e) => fe.handleError(e, toast.error),
  });

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="label-cap">Khata</div><h1 className="font-heading text-3xl mt-1">Clients</h1></div>
        <button data-testid="add-client-btn" onClick={openNew} className={btnAccent + " flex items-center gap-2"}><Plus size={16} /> Add Client</button>
      </div>

      {canManage && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Owed to You" value={fmtMoney(outstanding?.total || 0)} icon={HandCoins} tone={(outstanding?.total || 0) > 0 ? "danger" : "forest"} hint={`${outstanding?.clients?.length || 0} customers owing`} />
          <Stat label="Store Credit Held" value={fmtMoney(outstanding?.advances_total || 0)} icon={Wallet} hint={`${outstanding?.advances?.length || 0} with advance`} />
          <Stat label="Directory" value={total} icon={Users} hint="clients on record" />
        </div>
      )}

      <div className="card-soft p-4">
        <div className="relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-agri-ink-2" />
          <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, area…" className={inputCls + " pl-9"} />
        </div>
      </div>

      <div className="text-xs text-agri-ink-2">{total} client{total === 1 ? "" : "s"}</div>
      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Client</th>
            <th className={thCls}>Phone</th>
            <th className={thCls}>Area</th>
            <th className={thNumCls}>Credit Limit</th>
            <th className={thNumCls}>Balance</th>
            <th className={thCls}>Last Txn</th>
            <th className={thCls}>Status</th>
            {isAdmin && <th className={thNumCls}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {clients.length === 0 && <EmptyRow colSpan={isAdmin ? 8 : 7} icon={Users}>No clients found.</EmptyRow>}
          {clients.map((c) => (
            <tr key={c.id} className={rowCls}>
              <td className={`${tdCls} rt-primary`}><Link to={`/clients/${c.id}`} data-testid={`client-link-${c.id}`} className="font-medium text-agri-forest hover:underline">{c.name}</Link></td>
              <td data-label="Phone" className={`${tdCls} text-agri-ink-2`}>{c.phone}</td>
              <td data-label="Area" className={tdCls}>{c.area}</td>
              <td data-label="Credit Limit" className={tdNumCls}>{fmtMoney(c.credit_limit)}</td>
              <td data-label="Balance" className={`${tdNumCls} font-semibold ${c.balance > 0.001 ? "text-agri-danger" : c.balance < -0.001 ? "text-agri-forest" : "text-agri-ink-2"}`}>
                {c.balance < -0.001 ? `${fmtMoney(-c.balance)} adv` : fmtMoney(c.balance)}
              </td>
              <td data-label="Last Txn" className={`${tdCls} text-xs text-agri-ink-2`}>{c.last_transaction ? new Date(c.last_transaction).toLocaleDateString() : "—"}</td>
              <td data-label="Status" className={tdCls}><StatusBadge status={c.status} /></td>
              {isAdmin && (
                <td data-label="Actions" className={`${tdCls} text-right whitespace-nowrap rt-actions`}>
                  <button title="Edit" onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-black/5 text-agri-ink-2 hover:text-agri-forest align-middle"><Edit2 size={15} /></button>
                  <button title="Delete" onClick={() => window.confirm(`Delete client "${c.name}"?`) && del.mutate(c.id)} className="p-1.5 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger ml-1 align-middle"><Trash2 size={15} /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Pagination page={page} pageSize={PAGE} total={total} onPage={setPage} />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Client" : "Add Client"}>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Name</label>
            <input data-testid="client-name-input" required value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); fe.clearField("name"); }}
              className={inputCls + (fe.fieldError("name") ? " " + inputErrCls : "")} />
            <FieldError>{fe.fieldError("name")}</FieldError>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>CNIC (optional)</label><input value={form.cnic} onChange={(e) => setForm({ ...form, cnic: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Area / Village</label><input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Credit limit</label><NumberInput value={form.credit_limit} onChange={(n) => setForm({ ...form, credit_limit: n })} /></div>
          </div>
          <div><label className={labelCls}>Address</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button><button data-testid="client-save-btn" className={btnPrimary} disabled={save.isPending}>{editing ? "Update Client" : "Add Client"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
